import { useEffect, useState, useCallback } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { ErrorBanner } from "../components/ErrorBanner";
import { formatDate } from "../lib/utils";
import type { ShopRegistration } from "../lib/types";

const PAGE_SIZE = 50n;

export function RegistrationsPage() {
  const { actor } = useAdmin();
  const [items, setItems] = useState<ShopRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);

  const fetchPage = useCallback(async (cursor: string | null) => {
    if (!actor) return;
    setLoading(true);
    setError("");
    try {
      const res: { ok?: { items: ShopRegistration[]; nextCursor: [] | [string] }; err?: unknown } =
        await actor.listShopRegistrations(cursor ? [cursor] : [], PAGE_SIZE);
      if ("ok" in res) {
        setItems(res.ok!.items);
        setNextCursor(res.ok!.nextCursor[0] ?? null);
      } else {
        setError("Failed to load registrations");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  function nextPage() {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, nextCursor]);
    fetchPage(nextCursor);
  }

  function prevPage() {
    if (cursorStack.length <= 1) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    fetchPage(newStack[newStack.length - 1]);
  }

  const hasPrev = cursorStack.length > 1;

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 24 }}>Registrations</h1>

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      {loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No registrations yet.</p>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shop</th>
                <th>Registered</th>
                <th>Via</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((reg) => {
                const isFree = reg.registrationCode[0] != null;
                const detail = reg.registrationCode[0] ?? reg.mpesaRef[0];
                return (
                  <tr key={String(reg.id)}>
                    <td className="mono" style={{ color: "var(--color-muted)" }}>
                      {String(reg.id)}
                    </td>
                    <td className="mono" style={{ fontWeight: 500 }}>
                      TF{String(reg.shopNumber)}
                    </td>
                    <td style={{ color: "var(--color-muted)", fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(reg.created)}
                    </td>
                    <td>
                      <ViaBadge free={isFree} />
                    </td>
                    <td>
                      {detail ? (
                        <span className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>
                          {detail}
                        </span>
                      ) : (
                        <span style={{ color: "var(--color-muted)", fontSize: 12 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(hasPrev || nextCursor) && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button onClick={prevPage} disabled={!hasPrev} style={pageBtnStyle(!hasPrev)}>
            ← Prev
          </button>
          <button onClick={nextPage} disabled={!nextCursor} style={pageBtnStyle(!nextCursor)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function ViaBadge({ free }: { free: boolean }) {
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: 4,
      background: free ? "rgba(0,232,122,0.12)" : "rgba(59,130,246,0.12)",
      color: free ? "var(--color-accent)" : "#60a5fa",
      border: `1px solid ${free ? "rgba(0,232,122,0.3)" : "rgba(59,130,246,0.3)"}`,
    }}>
      {free ? "Free Code" : "M-Pesa"}
    </span>
  );
}

function pageBtnStyle(disabled: boolean | null): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 4,
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: disabled ? "var(--color-muted)" : "var(--color-text)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13,
    opacity: disabled ? 0.5 : 1,
  };
}
